import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SiteShell } from "@/components/site-shell";
import {
  Search,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  FileText,
  Heart,
  Phone,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: "FAQ & 80G Help — Manavta" },
      {
        name: "description",
        content:
          "Everything you need to know about donating, claiming 80G deductions, in-kind giving, and more.",
      },
    ],
  }),
  component: FAQ,
});

const SECTIONS = [
  {
    id: "donate",
    icon: "💝",
    label: "How to Donate",
    items: [
      {
        q: "How do I make a donation?",
        a: "Visit the Donate page, choose a cause, campaign, or institution, select your amount, and complete payment via UPI, card, or net banking. You'll receive an 80G receipt instantly.",
      },
      {
        q: "Can I donate without creating an account?",
        a: "Yes — one-time donations require no account. However, creating an account lets you download receipts, track your giving history, and manage recurring donations.",
      },
      {
        q: "What payment methods are accepted?",
        a: "We accept UPI, Credit Card, Debit Card, Net Banking, Wallets, and EMI. NRI donors can donate via international cards or wire transfer through our FCRA account.",
      },
      {
        q: "Can I donate monthly (recurring)?",
        a: "Yes. When donating, select 'Monthly' frequency. A UPI Autopay mandate will be set up for automatic deduction on the same date each month. You can cancel anytime from your donor portal.",
      },
      {
        q: "Is there a minimum donation amount?",
        a: "The minimum donation is ₹100. For 80G eligibility, donations must be ₹500 or above and PAN must be provided.",
      },
    ],
  },
  {
    id: "80g",
    icon: "📋",
    label: "80G Tax Deduction",
    items: [
      {
        q: "What is Section 80G?",
        a: "Section 80G of the Income Tax Act allows Indian taxpayers to claim a deduction of 50% or 100% of the donated amount from their taxable income, depending on the institution's approval type. Manavta holds 80G approval — your donations are eligible.",
      },
      {
        q: "How much tax can I save with an 80G donation?",
        a: "If you're in the 30% tax bracket and donate ₹10,000, your deduction reduces taxable income by ₹5,000 (50% deduction), saving you up to ₹1,500 in tax. Higher donations mean higher savings.",
      },
      {
        q: "Do I need a PAN to get an 80G receipt?",
        a: "Yes. PAN is mandatory for generating an 80G receipt as per Income Tax Department requirements. Your PAN is used only for receipt generation and is stored securely.",
      },
      {
        q: "When will I receive my 80G receipt?",
        a: "Your 80G receipt is generated automatically within 60 seconds of a successful donation. It is emailed to you and also available in your donor portal under 'My Receipts'.",
      },
      {
        q: "What is Form 10BE and do I need it?",
        a: "Form 10BE is the annual statement issued by the institution to the Income Tax Department, detailing all donations received. It is required for claiming 80G deductions while filing your income tax return from FY 2021-22 onwards. Manavta generates and files this on behalf of all partner institutions.",
      },
      {
        q: "How do I claim 80G deduction while filing ITR?",
        a: "Step 1: Collect your 80G receipts from the donor portal. Step 2: In ITR, go to 'Schedule VIA – Deductions'. Step 3: Under Section 80G, enter the institution's name, PAN, address, and amount donated. Step 4: Submit. Your CA or tax filing software (ClearTax, Quicko) can also auto-populate this if you share your receipts.",
      },
    ],
  },
  {
    id: "inkind",
    icon: "📦",
    label: "In-Kind Donations",
    items: [
      {
        q: "What is an In-Kind (Vastu Daan) donation?",
        a: "In-kind donations are physical item contributions — fodder, medicines, clothing, food, equipment — given directly to an institution instead of money. The institution receives the item and you receive an acknowledgement.",
      },
      {
        q: "What items can I donate in kind?",
        a: "For gaushalas and panjrapoles: green fodder, dry straw, medicines, supplements, tarpaulin sheets. For bird hospitals: bird grains, grit, gauze rolls. For old age homes: rice, dal, cooking oil, clothing, blankets, toiletries, wheelchairs. You can also describe your own item freely.",
      },
      {
        q: "How do I arrange delivery of my in-kind donation?",
        a: "When filling the in-kind form, choose your fulfillment method: (a) I will deliver myself — contact the institution directly, (b) Local vendor sourcing — the institution coordinates with a local supplier, or (c) Platform-arranged pickup — available in select cities for large donations.",
      },
      {
        q: "Will I get an 80G receipt for an in-kind donation?",
        a: "Yes, for in-kind donations valued at ₹500 or more, an acknowledgement certificate is issued. Whether this is fully 80G-deductible depends on the nature of the item and the institution's approval terms. Consult your CA for advice on claiming specific in-kind deductions.",
      },
      {
        q: "How do I know my in-kind donation was received?",
        a: "Once the institution confirms receipt, they upload a photo proof from their console. You'll receive a WhatsApp notification and email with the photo. Your donor portal also shows the status — Pledged, Notified, Received, or Photo Confirmed.",
      },
    ],
  },
  {
    id: "institutions",
    icon: "🏛️",
    label: "Institutions & Verification",
    items: [
      {
        q: "How are institutions verified?",
        a: "Every institution goes through a manual verification process: (1) Registration certificate and 80G/12A documents are reviewed, (2) An AI fraud risk score is assigned, (3) A foundation team member reviews and approves or rejects the application. Only verified institutions appear publicly.",
      },
      {
        q: "Can I donate directly to a specific gaushala without choosing a campaign?",
        a: "Yes. On any institution's profile page, click 'Donate Directly to This Gaushala'. Your donation goes into that institution's general fund, which they can use according to their operational needs.",
      },
      {
        q: "How do I register my Gaushala or Panjrapol?",
        a: "Visit /register-institution and complete the multi-step application form. You'll need your institution's registration certificate, 80G and 12A certificates, animal count details, and bank account information for payout routing. Applications are reviewed within 3–5 business days.",
      },
      {
        q: "How do institutions receive the donations?",
        a: "Donations are collected by the platform and settled to the institution's registered bank account on a regular cycle, after the institution submits an expenditure report showing how previous funds were used. This ensures transparency for donors.",
      },
    ],
  },
  {
    id: "sos",
    icon: "🚨",
    label: "SOS Animal Rescue",
    items: [
      {
        q: "What is the 24x7 SOS rescue desk?",
        a: "Manavta operates a 24x7 emergency dispatch service for injured animals. If you spot an injured cow, bird, dog, or other animal, fill in the SOS form with location and condition details and a rescue team is dispatched immediately.",
      },
      {
        q: "Is the rescue service free?",
        a: "Yes. The rescue service is fully funded through platform donations. There is no charge to the person reporting the incident or the animal.",
      },
      {
        q: "How long does it take for a rescue team to arrive?",
        a: "Average response time across Gujarat is 18 minutes for urban areas. Rural areas may take longer depending on the nearest available rescue unit.",
      },
      {
        q: "What information should I have ready when filing an SOS?",
        a: "Ideally: exact location with landmark or Google Maps link, animal species, condition (injured, unconscious, entangled), your phone number for the rescue team to contact you. A photo of the animal is helpful but not mandatory.",
      },
    ],
  },
];

function FAQ() {
  const [search, setSearch] = useState("");
  const [openSection, setOpenSection] = useState("80g");
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggleItem = (key: string) =>
    setOpenItems((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));

  const filtered = SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter(
      (i) =>
        search === "" ||
        i.q.toLowerCase().includes(search.toLowerCase()) ||
        i.a.toLowerCase().includes(search.toLowerCase()),
    ),
  })).filter((s) => s.items.length > 0);

  return (
    <SiteShell>
      {/* ── Hero ── */}
      <section className="border-b border-border/60 bg-gradient-to-br from-[var(--cream)] to-[var(--cream-2)]">
        <div className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--saffron-deep)]">
            Help Centre
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold sm:text-4xl text-[var(--maroon-deep)]">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 text-foreground/70">
            Everything about donating, 80G deductions, in-kind giving, and more.
          </p>

          {/* Search */}
          <div className="mx-auto mt-7 flex max-w-xl items-center gap-2 rounded-full border border-border/60 bg-white px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-[var(--maroon)]/20">
            <Search className="h-4 w-4 text-foreground/40 shrink-0" />
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              placeholder="Search questions — '80G receipt', 'in-kind', 'SOS'…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground/45"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="text-xs text-foreground/50 hover:text-foreground"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          {/* ── Sidebar nav ── */}
          <nav className="hidden lg:block">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.15em] text-foreground/50">
              Topics
            </p>
            <ul className="space-y-1">
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    onClick={() => setOpenSection(s.id)}
                    className={`w-full rounded-xl px-3 py-2.5 text-left text-sm font-medium transition flex items-center gap-2 ${
                      openSection === s.id
                        ? "bg-[var(--cream-2)] text-[var(--maroon)] font-bold"
                        : "text-foreground/70 hover:bg-muted/40 hover:text-foreground"
                    }`}
                  >
                    <span>{s.icon}</span>
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>

            {/* Download guide */}
            <div className="mt-8 rounded-2xl border border-[var(--saffron-deep)]/20 bg-[var(--saffron)]/8 p-4">
              <FileText className="h-5 w-5 text-[var(--saffron-deep)] mb-2" />
              <p className="text-xs font-bold text-[var(--maroon-deep)]">Download 80G Guide</p>
              <p className="mt-1 text-[11px] text-foreground/60">
                Step-by-step PDF for claiming 80G deduction in your ITR.
              </p>
              <button className="mt-3 w-full rounded-full border border-[var(--maroon)]/30 py-1.5 text-xs font-semibold text-[var(--maroon)] hover:bg-[var(--cream-2)] transition">
                Download PDF
              </button>
            </div>
          </nav>

          {/* ── Accordion ── */}
          <div className="space-y-8">
            {(search
              ? filtered
              : SECTIONS.filter((s) => s.id === openSection || search !== "")
            ).map((section) => {
              const items = search ? section.items : section.items;
              if (!search && section.id !== openSection) return null;
              return (
                <div key={section.id}>
                  <h2 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-[var(--maroon-deep)]">
                    <span>{section.icon}</span> {section.label}
                  </h2>
                  <div className="space-y-3">
                    {items.map((item, idx) => {
                      const key = `${section.id}-${idx}`;
                      const open = openItems.includes(key);
                      return (
                        <div
                          key={key}
                          className={`overflow-hidden rounded-2xl border transition ${open ? "border-[var(--maroon)]/30 bg-[var(--cream-2)]/60" : "border-border/60 bg-card"}`}
                        >
                          <button
                            onClick={() => toggleItem(key)}
                            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                          >
                            <span className="flex items-center gap-2.5 font-medium text-sm leading-snug">
                              <HelpCircle
                                className={`h-4 w-4 shrink-0 ${open ? "text-[var(--maroon)]" : "text-foreground/40"}`}
                              />
                              {item.q}
                            </span>
                            {open ? (
                              <ChevronUp className="h-4 w-4 shrink-0 text-[var(--maroon)]" />
                            ) : (
                              <ChevronDown className="h-4 w-4 shrink-0 text-foreground/40" />
                            )}
                          </button>
                          {open && (
                            <div className="border-t border-border/40 px-5 pb-5 pt-4">
                              <p className="text-sm leading-relaxed text-foreground/80">{item.a}</p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {/* ── Mobile section selector ── */}
            <div className="lg:hidden">
              <p className="mb-3 text-xs font-bold uppercase tracking-wide text-foreground/50">
                Browse by topic
              </p>
              <div className="flex flex-wrap gap-2">
                {SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setOpenSection(s.id)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${openSection === s.id ? "border-[var(--maroon)] bg-[var(--maroon)] text-white" : "border-border bg-white text-foreground/70"}`}
                  >
                    {s.icon} {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Still need help ── */}
        <div className="mt-12 rounded-3xl border border-border/60 bg-card p-8 text-center shadow-[var(--shadow-soft)]">
          <Sparkles className="mx-auto h-8 w-8 text-[var(--saffron-deep)]" />
          <h3 className="mt-3 font-display text-xl font-bold text-[var(--maroon-deep)]">
            Still have a question?
          </h3>
          <p className="mt-2 text-sm text-foreground/70">
            Our Seva AI chatbot can answer most questions instantly, or contact our team directly.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button className="inline-flex items-center gap-2 rounded-full bg-[var(--maroon)] px-6 py-2.5 text-sm font-bold text-white hover:bg-[var(--maroon-deep)] transition">
              <Sparkles className="h-4 w-4" /> Ask Seva AI
            </button>
            <a
              href="tel:+919999999999"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-semibold hover:bg-muted transition"
            >
              <Phone className="h-4 w-4" /> Call helpline
            </a>
            <a
              href="mailto:help@manavta.org"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-semibold hover:bg-muted transition"
            >
              <Heart className="h-4 w-4" /> Email us
            </a>
          </div>
        </div>
      </div>
    </SiteShell>
  );
}
